import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Input, Select, Typography, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { format } from 'date-fns/esm';
// eslint-disable-next-line import/no-duplicates
import { enUS, ptBR, es } from 'date-fns/locale';
// eslint-disable-next-line import/no-duplicates
import { addDays, subDays } from 'date-fns';

import Table from '@components/Table';
import useSearchParams from '@hooks/useSearchParams';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import { useAlarmMenuFilter } from '@hooks/useAlarmMenuFilter';
import rolesConstants from '@utils/rolesConstants';
import * as S from './Styles';

const { Search } = Input;

interface TimelineEvent {
  id: string;
  data: string;
  status: string;
  quantidade: number;
  level: string;
}

interface TimeframeDay {
  label: string;
  id: string;
  date: string;
  shortLabel?: string;
  endDate?: string;
  isShortPeriod: boolean;
}

interface AlarmData {
  id: string;
  alarme: string;
  tipo: string;
  valorDisparo: number;
  historicoDeAtivacoes: TimelineEvent[];
}

interface ApiResponse {
  content: AlarmData[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  totalElements: number;
}

const AlarmNotification: React.FC = () => {
  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'nomeAlarme',
      'tipoAlarme',
      'customerIdString',
      'status',
      'level',
      'lastTime',
      'nomeGrafico',
    ],
  });

  const {
    nomeAlarme,
    tipoAlarme,
    status,
    level,
    lastTime = '7d',
    nomeGrafico,
    page = 1,
    linesPerPage = 10,
  } = useMemo(() => searchParams, [searchParams]);

  const { t, i18n } = useTranslation(['alerts', 'attributes']);
  const currentLanguage = i18n.language;
  const timelineRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTooltipTimeout = useRef<NodeJS.Timeout | null>(null);

  const [nomeAlarmeValue, setNomeAlarmeValue] = useState('');
  const [activeMenuFilter, setActiveMenuFilter] = useState<string>(
    nomeGrafico || '',
  );
  const [tipoAlarmeValue, setTipoAlarmeValue] = useState<string | undefined>();
  const [levelValue, setLevelValue] = useState<string | undefined>();
  const [lastTimeValue, setLastTimeValue] = useState<string>('7d');
  const [mousePosition, setMousePosition] = useState<{ x: number }>({ x: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipData, setTooltipData] = useState<{
    date: string;
    events: TimelineEvent[];
    alarmName: string;
    threshold: number;
    tipo: string;
  } | null>(null);
  const [timeframeIndices, setTimeframeIndices] = useState<
    Record<string, number>
  >({});
  const [selectedAlarmIndex, setSelectedAlarmIndex] = useState<number | null>(
    null,
  );

  const {
    customer: { customerLogged },
  } = useContext(SessionContext);

  const {
    allMenuFilter,
    defaultFilterValue,
    hasSimcardsPermission,
    hasTransactionsPermission,
  } = useAlarmMenuFilter({
    simcardsPermission: rolesConstants.INSIGHTS_ALARM_NOTIFICATION,
    transactionsPermission: rolesConstants.INSIGHTS_TRANSACTIONS_VIEW,
  });

  const customerId = customerLogged?.id;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTooltipTimeout.current) {
        clearTimeout(hideTooltipTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    setActiveMenuFilter(nomeGrafico || '');
  }, [nomeGrafico]);

  useEffect(() => {
    setNomeAlarmeValue(nomeAlarme || '');
  }, [nomeAlarme]);

  useEffect(() => {
    setLevelValue(level);
  }, [level]);

  useEffect(() => {
    setTipoAlarmeValue(tipoAlarme);
  }, [tipoAlarme]);

  useEffect(() => {
    setLastTimeValue(lastTime || '7d');
  }, [lastTime]);

  const { data: apiResponse, isLoading } = useSwr<ApiResponse>(
    customerId
      ? '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar/alarmes_historico'
      : null,
    {
      params: {
        ...(activeMenuFilter ? { nomeGrafico: activeMenuFilter } : {}),
        ...(nomeAlarme ? { nomeAlarme } : {}),
        ...(status ? { status } : {}),
        ...(level ? { level } : {}),
        ...(tipoAlarme ? { tipoAlarme } : {}),
        page: Number(page),
        size: Number(linesPerPage),
        lastTime: lastTimeValue,
      },
      headers: {
        customerId,
      },
    },
  );

  const lastTimeOptions = useMemo(
    () => [
      {
        label: t('alerts:timeline.last-5-minutes', { minutes: 5 }),
        value: '5m',
      },
      {
        label: t('alerts:timeline.last-15-minutes', { minutes: 15 }),
        value: '15m',
      },
      {
        label: t('alerts:timeline.last-30-minutes', { minutes: 30 }),
        value: '30m',
      },
      { label: t('alerts:timeline.last-1-hour', { days: 1 }), value: '1h' },
      {
        label: t('alerts:timeline.last-24-hours', { hours: 24 }),
        value: '24h',
      },
      { label: t('alerts:timeline.last-week', { days: 7 }), value: '7d' },
      {
        label: t('alerts:timeline.last-two-weeks', { days: 14 }),
        value: '14d',
      },
      {
        label: t('alerts:timeline.last-three-weeks', { days: 21 }),
        value: '21d',
      },
      { label: t('alerts:timeline.last-month', { days: 30 }), value: '30d' },
      {
        label: t('alerts:timeline.last-two-months', { days: 60 }),
        value: '60d',
      },
      {
        label: t('alerts:timeline.last-three-months', { days: 90 }),
        value: '90d',
      },
    ],
    [t],
  );

  const getDateLocale = useCallback((language: string) => {
    switch (language) {
      case 'en':
        return enUS;
      case 'es':
        return es;
      default:
        return ptBR;
    }
  }, []);

  const generateTimeframes = useCallback(
    (period: string) => {
      const today = new Date();
      const dateLocale = getDateLocale(currentLanguage);

      if (
        period === '5m' ||
        period === '15m' ||
        period === '30m' ||
        period === '1h' ||
        period === '24h'
      ) {
        const now = new Date();
        const startTime = new Date(now);
        let periodLabel = '';

        if (period === '5m') {
          startTime.setMinutes(startTime.getMinutes() - 5);
          periodLabel = t('alerts:timeline.last-5-minutes');
        } else if (period === '15m') {
          startTime.setMinutes(startTime.getMinutes() - 15);
          periodLabel = t('alerts:timeline.last-15-minutes');
        } else if (period === '30m') {
          startTime.setMinutes(startTime.getMinutes() - 30);
          periodLabel = t('alerts:timeline.last-30-minutes');
        } else if (period === '1h') {
          startTime.setHours(startTime.getHours() - 1);
          periodLabel = t('alerts:timeline.last-1-hour');
        } else if (period === '24h') {
          startTime.setHours(startTime.getHours() - 24);
          periodLabel = t('alerts:timeline.last-24-hours');
        }

        const startTimeLabel = format(startTime, 'HH:mm', {
          locale: dateLocale,
        });
        const endTimeLabel = format(now, 'HH:mm', { locale: dateLocale });

        return [
          {
            name: periodLabel,
            days: [
              {
                label: `${startTimeLabel} - ${endTimeLabel}`,
                id: `period-${period}`,
                date: startTime.toISOString(),
                endDate: now.toISOString(),
                isShortPeriod: true,
              } as TimeframeDay,
            ],
          },
        ];
      }

      let daysToShow = 7;

      if (period.endsWith('d')) {
        daysToShow = parseInt(period.replace('d', ''), 10);
      }

      const maxDaysToDisplay = 8;
      const frames = [];

      const currentFrameDays = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < maxDaysToDisplay; i++) {
        const day = subDays(today, i);
        const dayLabel =
          i === 0
            ? t('alerts:timeline.today')
            : format(day, 'EEE dd', { locale: dateLocale });

        currentFrameDays.unshift({
          label: dayLabel,
          shortLabel: format(day, 'dd', { locale: dateLocale }),
          id: `day-${i}`,
          date: format(day, 'yyyy-MM-dd'),
          isShortPeriod: false,
          endDate: undefined,
        });
      }

      frames.push({
        name: t('alerts:timeline.current-period'),
        days: currentFrameDays.slice(0, maxDaysToDisplay),
      });

      const totalFrames = Math.ceil(daysToShow / maxDaysToDisplay);

      // eslint-disable-next-line no-plusplus
      for (let frame = 1; frame < totalFrames; frame++) {
        const frameDays = [];
        const frameStartDay = subDays(today, frame * maxDaysToDisplay);

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < maxDaysToDisplay; i++) {
          const day = addDays(frameStartDay, i);
          frameDays.push({
            label: format(day, 'EEE dd', { locale: dateLocale }),
            shortLabel: format(day, 'dd', { locale: dateLocale }),
            id: `day-${frame}-${i}`,
            date: format(day, 'yyyy-MM-dd'),
            isShortPeriod: false,
            endDate: undefined,
          });
        }

        frames.push({
          name: `${format(new Date(frameDays[0].date), 'dd MMM', {
            locale: dateLocale,
          })} - ${format(
            new Date(frameDays[frameDays.length - 1].date),
            'dd MMM',
            {
              locale: dateLocale,
            },
          )}`,
          days: frameDays,
        });
      }

      return frames;
    },
    [currentLanguage, t, getDateLocale],
  );

  const timeframes = useMemo(
    () => generateTimeframes(lastTimeValue),
    [generateTimeframes, lastTimeValue],
  );

  useEffect(() => {
    if (apiResponse?.content) {
      const newIndices: Record<string, number> = {};
      apiResponse.content.forEach(alarm => {
        newIndices[alarm.id] = 0;
      });
      setTimeframeIndices(newIndices);
    }
  }, [lastTimeValue, apiResponse?.content]);

  const alarmTypeOptions = useMemo(
    () => [
      {
        label: t('alerts:table-filter.by-alarm-type-threshold'),
        value: 'threshold',
      },
      {
        label: t(
          'alerts:table-filter.by-alarm-type-variation-percentual-history',
        ),
        value: 'variacao_percentual',
      },
      {
        label: t('alerts:table-filter.by-alarm-type-seasonal'),
        value: 'sazonal',
      },
    ],
    [t],
  );

  useEffect(() => {
    if (
      activeMenuFilter === 'simcard_produto' &&
      !hasSimcardsPermission &&
      hasTransactionsPermission
    ) {
      setActiveMenuFilter(defaultFilterValue);
      setSearchParams({ nomeGrafico: defaultFilterValue, page: 1 });
    }
  }, [
    activeMenuFilter,
    hasSimcardsPermission,
    hasTransactionsPermission,
    defaultFilterValue,
    setSearchParams,
  ]);

  const dateFormatConfig = useMemo(() => {
    if (currentLanguage === 'en') {
      return {
        locale: enUS,
        dateFormat: 'MM/dd/yyyy HH:mm:ss a',
      };
    }
    if (currentLanguage === 'es') {
      return {
        locale: es,
        dateFormat: 'dd/MM/yyyy HH:mm:ss',
      };
    }
    return {
      locale: ptBR,
      dateFormat: 'dd/MM/yyyy HH:mm:ss',
    };
  }, [currentLanguage]);

  const formatDate = useCallback(
    (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return format(date, dateFormatConfig.dateFormat, {
        locale: dateFormatConfig.locale,
      });
    },
    [dateFormatConfig],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent, alarmIndex: number) => {
      if (!timelineRef.current || !apiResponse) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const relativeX = x / rect.width;

      const alarmId = apiResponse.content[alarmIndex].id;
      const currentIndex = timeframeIndices[alarmId] || 0;
      const currentTimeframe = timeframes[currentIndex];
      const currentDay = currentTimeframe.days[0];

      let date: Date;

      if (currentDay.isShortPeriod) {
        const startDate = new Date(currentDay.date);
        const endDate = new Date(currentDay.endDate ?? new Date());
        const timeDiff = endDate.getTime() - startDate.getTime();
        date = new Date(startDate.getTime() + timeDiff * relativeX);
      } else {
        const segmentIndex = Math.min(
          Math.floor(relativeX * currentTimeframe.days.length),
          currentTimeframe.days.length - 1,
        );
        const segment = currentTimeframe.days[segmentIndex];

        const segmentWidth = rect.width / currentTimeframe.days.length;
        const segmentStart = segmentIndex * segmentWidth;
        const positionInSegment = (x - segmentStart) / segmentWidth;

        const [year, month, dayNum] = segment.date.split('-').map(Number);
        date = new Date(
          year,
          month - 1,
          dayNum,
          Math.floor(positionInSegment * 24),
          Math.floor((positionInSegment * 24 * 60) % 60),
          Math.floor((positionInSegment * 24 * 60 * 60) % 60),
        );
      }

      const formattedDate = formatDate(date.toISOString());

      const alarmData = apiResponse.content[alarmIndex];

      const activeEvents = alarmData.historicoDeAtivacoes.filter(event => {
        const eventDate = new Date(event.data);

        if (
          lastTimeValue === '5m' ||
          lastTimeValue === '15m' ||
          lastTimeValue === '30m'
        ) {
          const diffInMinutes = Math.abs(
            (eventDate.getTime() - date.getTime()) / (60 * 1000),
          );
          return diffInMinutes <= 1;
        }
        if (lastTimeValue === '1h') {
          const diffInMinutes = Math.abs(
            (eventDate.getTime() - date.getTime()) / (60 * 1000),
          );
          return diffInMinutes <= 5;
        }
        if (lastTimeValue === '24h') {
          return (
            eventDate.getDate() === date.getDate() &&
            eventDate.getMonth() === date.getMonth() &&
            eventDate.getFullYear() === date.getFullYear() &&
            eventDate.getHours() === date.getHours()
          );
        }
        return (
          eventDate.getFullYear() === date.getFullYear() &&
          eventDate.getMonth() === date.getMonth() &&
          eventDate.getDate() === date.getDate()
        );
      });

      setMousePosition({ x });
      setTooltipPosition({ x: e.clientX - 150, y: e.clientY });
      setTooltipData({
        date: formattedDate,
        events: activeEvents,
        alarmName: alarmData.alarme,
        threshold: alarmData.valorDisparo,
        tipo: alarmData.tipo,
      });
      setTooltipVisible(true);
      setSelectedAlarmIndex(alarmIndex);
    },
    [timeframeIndices, timeframes, apiResponse, formatDate, lastTimeValue],
  );

  const handleMouseLeave = useCallback(() => {
    // Delay hiding tooltip to allow mouse to move to tooltip
    hideTooltipTimeout.current = setTimeout(() => {
      setMousePosition({ x: 0 });
      setTooltipVisible(false);
      setSelectedAlarmIndex(null);
    }, 200);
  }, []);

  const handleTooltipMouseEnter = useCallback(() => {
    // Cancel hiding when mouse enters tooltip
    if (hideTooltipTimeout.current) {
      clearTimeout(hideTooltipTimeout.current);
      hideTooltipTimeout.current = null;
    }
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    // Hide tooltip when mouse leaves it
    setMousePosition({ x: 0 });
    setTooltipVisible(false);
    setSelectedAlarmIndex(null);
  }, []);

  const navigatePrevious = useCallback(
    (alarmId: string) => {
      setTimeframeIndices(prev => {
        const currentIndex = prev[alarmId] || 0;
        return {
          ...prev,
          [alarmId]: Math.min(currentIndex + 1, timeframes.length - 1),
        };
      });
    },
    [timeframes.length],
  );

  const navigateNext = useCallback((alarmId: string) => {
    setTimeframeIndices(prev => {
      const currentIndex = prev[alarmId] || 0;
      return {
        ...prev,
        [alarmId]: Math.max(currentIndex - 1, 0),
      };
    });
  }, []);

  useEffect(() => {
    if (tooltipRef.current && tooltipVisible) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = tooltipPosition.x;
      let adjustedY = tooltipPosition.y;

      // Ajusta posição horizontal se sair da tela pela direita
      if (tooltipPosition.x + tooltipRect.width > viewportWidth) {
        adjustedX = viewportWidth - tooltipRect.width - 20;
      }

      // Ajusta posição horizontal se sair da tela pela esquerda
      if (adjustedX < 10) {
        adjustedX = 10;
      }

      // Ajusta posição vertical se sair da tela pela parte inferior
      if (tooltipPosition.y + tooltipRect.height > viewportHeight) {
        adjustedY = viewportHeight - tooltipRect.height - 20;
      }

      // Ajusta posição vertical se sair da tela pela parte superior
      if (adjustedY < 10) {
        adjustedY = 10;
      }

      if (adjustedX !== tooltipPosition.x || adjustedY !== tooltipPosition.y) {
        setTooltipPosition({ x: adjustedX, y: adjustedY });
      }
    }
  }, [tooltipVisible, tooltipPosition]);

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.graph-name-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={allMenuFilter}
            style={{ width: '100%' }}
            showSearch
            value={activeMenuFilter}
            onChange={value => {
              setActiveMenuFilter(value);
              setSearchParams({ nomeGrafico: value, page: 1 });
            }}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.period-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={lastTimeOptions}
            style={{ width: '100%' }}
            value={lastTimeValue}
            onChange={value => {
              setLastTimeValue(value);
              setSearchParams({
                lastTime: value,
                page: 1,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.alert-name-label'),
        element: (
          <Search
            onSearch={value => {
              setSearchParams({ nomeAlarme: value, page: 1 });
            }}
            onChange={({ target: { value } }) => setNomeAlarmeValue(value)}
            value={nomeAlarmeValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.alarm-type-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={alarmTypeOptions}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={tipoAlarmeValue}
            onChange={value => {
              setTipoAlarmeValue(value);
              setSearchParams({
                tipoAlarme: value,
                page: 1,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.status-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: t('alerts:table-filter.by-status-active'),
                value: 'ativo',
              },
              {
                label: t('alerts:table-filter.by-status-inactive'),
                value: 'inativo',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={status}
            onChange={value => {
              setSearchParams({
                status: value,
                page: 1,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.level-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: t('alerts:table-filter.by-level-critical') || 'CRITICO',
                value: 'CRITICAL',
              },
              {
                label: t('alerts:table-filter.by-level-ok') || 'OK',
                value: 'OK',
              },
              {
                label: t('alerts:table-filter.by-level-crit') || 'CRIT',
                value: 'CRIT',
              },
              {
                label: t('alerts:table-filter.by-level-recovery') || 'RECOVERY',
                value: 'RECOVERY',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={levelValue}
            onChange={value => {
              setLevelValue(value);
              setSearchParams({
                level: value,
                page: 1,
              });
            }}
          />
        ),
      },
    ],
    [
      t,
      allMenuFilter,
      activeMenuFilter,
      lastTimeOptions,
      lastTimeValue,
      nomeAlarmeValue,
      alarmTypeOptions,
      tipoAlarmeValue,
      status,
      levelValue,
      setSearchParams,
    ],
  );

  const renderAlarmTimeline = useCallback(
    (alarm: AlarmData, index: number) => {
      const alarmEvents = alarm.historicoDeAtivacoes;
      const currentTimeframeIndex = timeframeIndices[alarm.id] || 0;

      const isShortPeriod =
        timeframes[currentTimeframeIndex].days.length === 1 &&
        timeframes[currentTimeframeIndex].days[0].isShortPeriod;

      return (
        <S.TimelineContainer
          ref={index === 0 ? timelineRef : undefined}
          key={`${alarm.id}-${index}`}
        >
          <S.TimelineSection>
            <S.TimelineTitle>
              {`${alarm.alarme} - ${
                alarm.tipo === 'QTD_MAX'
                  ? t('timeline.alarm-above')
                  : t('timeline.alarm-below')
              } ${alarm.valorDisparo}`}
            </S.TimelineTitle>
            <S.TimelineControls>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                style={{
                  height: '32px',
                  width: '32px',
                  padding: 0,
                  borderRadius: '50%',
                  visibility: isShortPeriod ? 'hidden' : 'visible',
                }}
                onClick={() => navigatePrevious(alarm.id)}
                disabled={currentTimeframeIndex >= timeframes.length - 1}
                className="previous-timeline-button"
              />

              <S.TimelineContent
                onMouseMove={e => handleMouseMove(e, index)}
                onMouseLeave={handleMouseLeave}
              >
                <S.DateLabelsContainer
                  className={isShortPeriod ? 'single-period' : ''}
                >
                  {timeframes[currentTimeframeIndex].days.map(day => (
                    <S.DateLabel key={day.id}>
                      <span className="label-desktop">{day.label}</span>
                      <span className="label-mobile">{day.shortLabel}</span>
                    </S.DateLabel>
                  ))}
                </S.DateLabelsContainer>

                <S.TimelineBars
                  className={isShortPeriod ? 'single-period' : ''}
                >
                  {timeframes[currentTimeframeIndex].days.map(
                    (day, dayIndex) => {
                      if (isShortPeriod) {
                        const start = new Date(day.date);
                        const end = new Date(day.endDate ?? new Date());
                        const msInPeriod = end.getTime() - start.getTime();
                        return (
                          <S.TimelineBar
                            key={day.id}
                            isLast
                            className="single-period"
                          >
                            {alarmEvents.map((event, eventIndex) => {
                              const eventDate = new Date(event.data);
                              if (eventDate < start || eventDate > end)
                                return null;
                              const position =
                                ((eventDate.getTime() - start.getTime()) /
                                  msInPeriod) *
                                100;
                              return (
                                <S.EventIndicator
                                  // eslint-disable-next-line react/no-array-index-key, prefer-template
                                  key={event.id + '-' + eventIndex}
                                  startPosition={position}
                                  width={2}
                                >
                                  {event.level === 'CRITICAL' ||
                                  event.level === 'CRIT' ? (
                                    <div
                                      style={{
                                        width: '4px',
                                        height: '32px',
                                        backgroundColor: '#D2211D',
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '4px',
                                        height: '32px',
                                        backgroundColor: '#00BE14',
                                      }}
                                    />
                                  )}
                                </S.EventIndicator>
                              );
                            })}
                          </S.TimelineBar>
                        );
                      }

                      const [year, month, date] = day.date
                        .split('-')
                        .map(Number);
                      const dayStart = new Date(
                        year,
                        month - 1,
                        date,
                        0,
                        0,
                        0,
                        0,
                      );
                      const dayEnd = new Date(
                        year,
                        month - 1,
                        date,
                        23,
                        59,
                        59,
                        999,
                      );
                      const msInDay = dayEnd.getTime() - dayStart.getTime();
                      const eventsOfDay = alarmEvents.filter(event => {
                        const eventDate = new Date(event.data);
                        return eventDate >= dayStart && eventDate <= dayEnd;
                      });

                      return (
                        <S.TimelineBar
                          key={day.id}
                          isLast={
                            dayIndex ===
                            timeframes[currentTimeframeIndex].days.length - 1
                          }
                        >
                          {eventsOfDay.map((event, eventIndex) => {
                            const eventDate = new Date(event.data);
                            const position =
                              ((eventDate.getTime() - dayStart.getTime()) /
                                msInDay) *
                              100;
                            return (
                              <S.EventIndicator
                                // eslint-disable-next-line react/no-array-index-key, prefer-template
                                key={event.id + '-' + eventIndex}
                                startPosition={position}
                                width={2}
                              >
                                {event.level === 'CRITICAL' ||
                                event.level === 'CRIT' ? (
                                  <div
                                    style={{
                                      width: '4px',
                                      height: '32px',
                                      backgroundColor: '#D2211D',
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: '4px',
                                      height: '32px',
                                      backgroundColor: '#00BE14',
                                    }}
                                  />
                                )}
                              </S.EventIndicator>
                            );
                          })}
                        </S.TimelineBar>
                      );
                    },
                  )}
                </S.TimelineBars>

                {tooltipVisible && selectedAlarmIndex === index && (
                  <S.MousePositionIndicator x={mousePosition.x} />
                )}
              </S.TimelineContent>

              <Button
                type="text"
                icon={<ArrowRightOutlined />}
                style={{
                  height: '32px',
                  width: '32px',
                  padding: 0,
                  borderRadius: '50%',
                  visibility: isShortPeriod ? 'hidden' : 'visible',
                }}
                onClick={() => navigateNext(alarm.id)}
                disabled={currentTimeframeIndex <= 0}
                className="next-timeline-button"
              />
            </S.TimelineControls>
          </S.TimelineSection>

          {alarmEvents.length > 0 ? (
            <S.EventsListSection>
              <Collapse>
                <Collapse.Panel
                  key="event-list"
                  header={
                    <Typography.Title style={{ margin: 0, fontSize: '16px' }}>
                      {t('alerts:timeline.events-list')}
                    </Typography.Title>
                  }
                >
                  <Collapse
                    collapsible="disabled"
                    className="disabled-collapse"
                  >
                    {alarmEvents.map(event => (
                      <Collapse.Panel
                        key={`event-${event.id}-${event.data}-${event.level}`}
                        showArrow={false}
                        header={
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '24px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                              }}
                            >
                              <S.EventType>
                                <S.EventTypeTag
                                  type={
                                    event.level === 'CRITICAL' ||
                                    event.level === 'CRIT'
                                      ? 'outage'
                                      : 'operational'
                                  }
                                >
                                  {/* @ts-ignore */}
                                  {(() => {
                                    if (event.level === 'CRITICAL')
                                      return t('alerts:timeline.alerting');
                                    if (event.level === 'OK')
                                      return t('alerts:timeline.normal');
                                    if (event.level === 'CRIT')
                                      return t(
                                        'alerts:table-filter.by-level-crit',
                                      );
                                    return t(
                                      'alerts:table-filter.by-level-recovery',
                                    );
                                  })()}
                                </S.EventTypeTag>
                              </S.EventType>
                              {event.level !== 'CRIT' && (
                                <S.EventContent>
                                  <S.EventId>
                                    {`${t(
                                      'alerts:timeline.alarm-triggered-value',
                                    )} ${event.quantidade}`}
                                  </S.EventId>
                                </S.EventContent>
                              )}
                            </div>
                            <div>
                              <span style={{ marginRight: '6px' }}>
                                {t('alerts:timeline.alarm-triggered')}
                              </span>
                              <span style={{ color: '#8c8c8c' }}>
                                {formatDate(event.data)}
                              </span>
                            </div>
                          </div>
                        }
                      />
                    ))}
                  </Collapse>
                </Collapse.Panel>
              </Collapse>
            </S.EventsListSection>
          ) : (
            <Typography
              style={{
                fontSize: '12px',
                textAlign: 'center',
              }}
            >
              {t('alerts:timeline.no-events-alarm')}
              {lastTimeOptions
                .find(option => option.value === lastTimeValue)
                ?.label.toLowerCase()}
            </Typography>
          )}
        </S.TimelineContainer>
      );
    },
    [
      timeframes,
      timeframeIndices,
      t,
      navigatePrevious,
      handleMouseLeave,
      tooltipVisible,
      selectedAlarmIndex,
      mousePosition.x,
      navigateNext,
      lastTimeOptions,
      handleMouseMove,
      formatDate,
      lastTimeValue,
    ],
  );

  return (
    <S.Container>
      <Table
        columns={[
          {
            title: '',
            dataIndex: 'content',
            key: 'content',
            render: (_, record) => record.content,
          },
        ]}
        data={
          apiResponse?.content.map((alarm, index) => {
            return {
              key: `${alarm.id}-${index}`,
              content: renderAlarmTimeline(alarm, index),
              id: `alarm-${index}-${alarm.id}`,
            };
          }) || []
        }
        loading={isLoading}
        title={t('alerts:titles.alarm-notification')}
        tableKey="alarm-notification-page"
        customRowKey="key"
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
          openByDefault: true,
        }}
        pagination={{
          page: Number(page),
          linesPerPage: Number(linesPerPage),
          total: apiResponse?.totalElements || 0,
        }}
        onTableChange={setSearchParams}
      />

      {tooltipVisible && tooltipData && (
        <S.Tooltip
          ref={tooltipRef}
          x={tooltipPosition.x}
          y={tooltipPosition.y}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <S.TooltipDate>{tooltipData.date}</S.TooltipDate>
          {tooltipData.events.length > 0 ? (
            tooltipData.events.map(event => (
              <S.TooltipEvent
                key={`tooltip-event-${event.id}-${event.data}-${event.level}-${event.quantidade}`}
              >
                <S.TooltipEventIcon>
                  {event.level === 'CRITICAL' || event.level === 'CRIT' ? (
                    <div
                      style={{
                        width: '4px',
                        height: '40px',
                        backgroundColor: '#D2211D',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '4px',
                        height: '40px',
                        backgroundColor: '#00BE14',
                      }}
                    />
                  )}
                </S.TooltipEventIcon>
                <S.TooltipEventContent>
                  <S.TooltipEventTitle>
                    {(() => {
                      const alertValue = t(
                        'alerts:timeline.alert-value',
                      ) as string;
                      const quantidade =
                        event.quantidade !== null ? event.quantidade : '';
                      if (event.level === 'CRITICAL')
                        return `${
                          t('alerts:timeline.alerting-critical') as string
                        } : ${alertValue} ${quantidade}`;
                      if (event.level === 'OK')
                        return `${
                          t('alerts:timeline.alerting-normal') as string
                        } : ${alertValue} ${quantidade}`;
                      if (event.level === 'CRIT')
                        return t('alerts:table-filter.by-level-crit') as string;
                      return `${
                        t('alerts:table-filter.by-level-recovery') as string
                      } : ${alertValue} ${quantidade}`;
                    })()}
                  </S.TooltipEventTitle>
                  <S.TooltipEventDate>
                    {formatDate(event.data)}
                  </S.TooltipEventDate>
                </S.TooltipEventContent>
              </S.TooltipEvent>
            ))
          ) : (
            <S.TooltipNoEvents>
              {/* @ts-ignore */}
              {t('alerts:timeline.no-events')}
            </S.TooltipNoEvents>
          )}
        </S.Tooltip>
      )}
    </S.Container>
  );
};

export default AlarmNotification;
